/* Millimili 3.0 — assets/product-video-feature.js
 * "In motion" renk bazlı ürün videosu bandı (sections/product-video-feature.liquid, ADDENDUM-product-video.md).
 *
 * Davranış:
 * - Seçili rengi Wonder'ın galerisiyle AYNI yerden okur: <variant-options> içindeki işaretli radyolar,
 *   AYNI olaydan dinler: variant-options'ın yaydığı "variantChangeEnd" (gallery.js süzmesini tetikleyen
 *   updateGallery() bu olaydan hemen önce çalışır; beden seçilmemişken de yayılır).
 * - Video ↔ renk eşlemesi gallery.js filterSlidesByOptions ile aynı fonksiyonla yapılır (varsa galerinin
 *   kendi metodu çağrılır, yoksa birebir kopyası): renk değeri küçük harf + boşluksuz, "#a|b", "#all".
 *   D18: renk başına TEK video, sıra: rengin kendi #etiketi → #all → (yalnız tek renkli üründe, data-single)
 *   etiketsiz video. Çok renkli üründe etiketsiz video gösterilmez. Her grupta medya sırasındaki ilki.
 * - Önizleme eşlemesi (D09/D21) ve "bantta gösterilmeyecek" listesi (D21) Liquid'de çözülür: şablonun data-alt'ı
 *   eşlenmiş alt metni taşır (renk etiketleri eşlemedeki etiketle değişmiş), dışlanan video hiç basılmaz. Bu dosya
 *   yalnız o şablonları okuduğu için varyant değişimi ilk açılışla aynı eşlemeyi ve dışlamayı uygular.
 * - Eşleşme yoksa bölüm hidden (boşluk dahil iz yok).
 * - Oynatma: görünür olunca (IntersectionObserver) oynar, çıkınca durur. prefers-reduced-motion'da
 *   otomatik oynatma yok; poster + oynat butonu. Kaynak ve poster, temanın low-power-video.js hattıyla
 *   gecikmeli bağlanır (data-wt-lazy / data-wt-src / data-wt-poster): LCP'ye bayt eklenmez.
 * - Buton üstteki satın al butonunun durumunu aynalar ("Select a size" / "Add to bag — fiyat" / "Sold out").
 *   Beden seçili değilse beden seçicisine götürür; seçiliyse sayfadaki satın al formunu kullanır.
 * Galeride video yok (D18, main-product.liquid basmıyor); bant galeriyle koordinasyon gerektirmez.
 */
(() => {
  if (customElements.get('product-video-feature')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // gallery.js filterSlidesByOptions ile birebir aynı normalleştirme (galeri yoksa kullanılan kopya)
  const norm = (s) => String(s || '').toLowerCase().replace(/\s/g, '');
  const hashtags = (alt) => (String(alt || '').match(/#[^\s#]+/g) || []).map((h) => h.slice(1).toLowerCase());
  function matchesLikeGallery(alt, options) {
    const lower = options.map(norm);
    const tags = hashtags(alt);
    if (tags.length === 0) return true;
    if (tags.some((t) => t.split('|').includes('all'))) return true;
    return tags.every((t) => t.split('|').some((v) => lower.includes(v.trim())));
  }

  const fmtTime = (sec) => {
    if (!isFinite(sec) || sec <= 0) return '';
    const s = Math.round(sec);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  class ProductVideoFeature extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.optionName = this.dataset.optionName;
      this.optionIndex = Math.max(0, Number(this.dataset.optionPosition || 1) - 1);
      this.templates = Array.from(this.querySelectorAll('template[data-pvf-video]'));
      try {
        this.colors = JSON.parse(this.querySelector('[data-pvf-colors]')?.textContent || '{}');
      } catch (e) {
        this.colors = {};
      }
      this.stage = this.querySelector('[data-pvf-stage]');
      this.playBtn = this.querySelector('[data-pvf-play]');
      this.muteBtn = this.querySelector('[data-pvf-mute]');
      this.fill = this.querySelector('[data-pvf-fill]');
      this.timeEl = this.querySelector('[data-pvf-time]');
      this.buyBtn = this.querySelector('[data-pvf-buy]');
      this.backLink = this.querySelector('[data-pvf-back]');
      this.currentId = null;
      this.userPaused = false;
      this.visible = false;

      this.onVariantChangeEnd = (e) => {
        const vo = this.variantOptions();
        if (vo && e.target !== vo && !vo.contains(e.target)) return;
        this.sync();
      };
      document.addEventListener('variantChangeEnd', this.onVariantChangeEnd);

      this.playBtn?.addEventListener('click', () => this.togglePlay());
      this.muteBtn?.addEventListener('click', () => this.toggleMute());
      this.buyBtn?.addEventListener('click', () => this.onBuy());
      this.backLink?.addEventListener('click', (e) => this.onBack(e));

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          for (const en of entries) {
            this.visible = en.isIntersecting;
            this.autoPlayPause();
          }
        }, { threshold: 0.25 });
        this.io.observe(this.querySelector('.pvf__media') || this);
      }

      this.mirrorBuy();
      const main = this.mainButton();
      if (main && 'MutationObserver' in window) {
        this.buyMo = new MutationObserver(() => this.mirrorBuy());
        this.buyMo.observe(main, { attributes: true, childList: true, subtree: true, characterData: true });
      }

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('variantChangeEnd', this.onVariantChangeEnd);
      this.io?.disconnect();
      this.buyMo?.disconnect();
    }

    variantOptions() {
      return document.querySelector(`variant-options[data-product-handle="${CSS.escape(this.handle)}"]`);
    }

    gallery() {
      const vo = this.variantOptions();
      const sid = vo?.dataset.section;
      return (sid && document.getElementById(`MediaGallery-${sid}`)) || document.querySelector('gallery-section[data-product-page]');
    }

    mainButton() {
      const vo = this.variantOptions();
      const sid = vo?.dataset.section;
      return (sid && document.getElementById(`ProductSubmitButton-${sid}`)) || document.querySelector('.wt-product__main [name="add"]');
    }

    selectedOptions() {
      const vo = this.variantOptions();
      if (!vo) return [];
      return Array.from(vo.querySelectorAll('.wt-product__option')).map((fs) => {
        const r = Array.from(fs.querySelectorAll('input')).find((i) => i.checked);
        return r ? r.value : '';
      });
    }

    // Seçili seçeneklere göre banttaki video şablonu (galerinin süzme fonksiyonuyla).
    // D18 sırası: rengin kendi etiketi → #all → tek renkli üründe etiketsiz.
    templateFor(options) {
      const g = this.gallery();
      const useGallery = g && typeof g.filterSlidesByOptions === 'function';
      let allT = null;
      let untaggedT = null;
      for (const t of this.templates) {
        const alt = t.dataset.alt || '';
        const tags = hashtags(alt);
        if (tags.length === 0) {
          if (this.hasAttribute('data-single') && !untaggedT) untaggedT = t; // tek renk: o renge ait
          continue;
        }
        let ok;
        if (useGallery) {
          const probe = document.createElement('li');
          const v = document.createElement('video');
          v.setAttribute('alt', alt);
          probe.appendChild(v);
          // featured_media_id = false: galerinin "öne çıkan medya her zaman kalır" kuralı sonda eşleşmesin
          ok = g.filterSlidesByOptions([probe], options, false, true).length === 1;
        } else {
          ok = matchesLikeGallery(alt, options);
        }
        if (!ok) continue;
        if (tags.some((x) => x.split('|').includes('all'))) {
          allT = allT || t;
          continue;
        }
        return t;
      }
      return allT || untaggedT;
    }

    sync() {
      const options = this.selectedOptions();
      const colorValue = options[this.optionIndex] || '';
      const t = colorValue ? this.templateFor(options) : null;
      if (!t) {
        this.clear();
        this.hidden = true;
        return;
      }
      const info = this.colors[norm(colorValue)] || { name: colorValue, hex: '' };
      this.querySelectorAll('[data-pvf-color-name]').forEach((el) => { el.textContent = info.name; });
      this.querySelectorAll('[data-pvf-dot]').forEach((el) => {
        el.style.background = info.hex && CSS.supports('color', info.hex) ? info.hex : '';
      });
      const body = this.querySelector('[data-pvf-body]');
      if (body && this.dataset.bodyTemplate) body.textContent = this.dataset.bodyTemplate.replace(/\{color\}/g, info.name.toLowerCase());
      if (t.dataset.mediaId !== this.currentId) this.mount(t, info.name);
      this.hidden = false;
      this.mirrorBuy();
    }

    clear() {
      const old = this.stage?.querySelector('video');
      if (old) { old.pause(); old.remove(); }
      this.currentId = null;
    }

    mount(t, colorName) {
      this.clear();
      const frag = t.content.cloneNode(true);
      const video = frag.querySelector('video');
      if (!video) return;
      // low-power-video.js hattı: kaynak ve poster görünürlüğe yakın bağlanır (LCP'yi etkilemez)
      const poster = video.getAttribute('poster');
      if (poster) { video.setAttribute('data-wt-poster', poster); video.removeAttribute('poster'); }
      video.querySelectorAll('source').forEach((s) => {
        s.setAttribute('data-wt-src', s.getAttribute('src'));
        s.removeAttribute('src');
      });
      video.querySelectorAll('img').forEach((i) => i.remove());
      video.setAttribute('data-wt-lazy', '1');
      video.removeAttribute('autoplay');
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('preload', 'none');
      video.removeAttribute('controls');
      video.setAttribute('aria-label', `${this.dataset.productTitle || ''} in ${colorName}, video`);
      video.classList.add('pvf__video');
      this.stage.appendChild(video);
      this.video = video;
      this.currentId = t.dataset.mediaId;
      this.userPaused = false;

      const dur = Number(t.dataset.duration || 0) / 1000;
      if (this.timeEl) this.timeEl.textContent = fmtTime(dur);
      if (this.fill) this.fill.style.width = '0%';
      video.addEventListener('timeupdate', () => {
        if (this.fill && video.duration) this.fill.style.width = `${(video.currentTime / video.duration) * 100}%`;
      });
      video.addEventListener('loadedmetadata', () => {
        if (this.timeEl) this.timeEl.textContent = fmtTime(video.duration);
      });
      video.addEventListener('play', () => this.setPlayState(true));
      video.addEventListener('pause', () => this.setPlayState(false));
      video.addEventListener('volumechange', () => this.setMuteState());
      this.setPlayState(false);
      this.setMuteState();
      // bağlanınca low-power-video.js DOM gözlemcisi yakalar; oynatmayı biz yönetiriz
      requestAnimationFrame(() => this.autoPlayPause());
    }

    autoPlayPause() {
      const v = this.video;
      if (!v || !v.isConnected || this.hidden) return;
      if (this.visible && !this.userPaused && !reduceMotion()) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else if (!this.visible && !v.paused) {
        v.pause();
      }
    }

    togglePlay() {
      const v = this.video;
      if (!v) return;
      if (v.paused) {
        this.userPaused = false;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        this.userPaused = true;
        v.pause();
      }
    }

    toggleMute() {
      const v = this.video;
      if (!v) return;
      v.muted = !v.muted;
      if (!v.muted && v.paused) {
        this.userPaused = false;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    }

    setPlayState(playing) {
      if (!this.playBtn) return;
      this.playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      this.playBtn.dataset.state = playing ? 'playing' : 'paused';
    }

    setMuteState() {
      if (!this.muteBtn || !this.video) return;
      const muted = this.video.muted;
      this.muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      this.muteBtn.dataset.state = muted ? 'muted' : 'sound';
    }

    // Üstteki satın al butonunun durumunu aynen yansıt
    mirrorBuy() {
      const b = this.buyBtn;
      const main = this.mainButton();
      if (!b || !main) return;
      const pending = main.hasAttribute('data-mm3-pending');
      const label = main.querySelector('.wt-btn__label span')?.textContent.trim() || '';
      const price = main.querySelector('wt-btn-price')?.textContent.trim() || '';
      let text = label;
      if (pending) text = main.dataset.mm3SelectLabel || label;
      else if (!main.disabled && price) text = `${label} — ${price}`;
      if (text && b.textContent !== text) b.textContent = text;
      b.disabled = !pending && main.disabled;
      b.dataset.state = pending ? 'pending' : main.disabled ? 'disabled' : 'ready';
    }

    onBuy() {
      const main = this.mainButton();
      if (!main) return;
      if (main.hasAttribute('data-mm3-pending')) {
        const size = document.querySelector('.mm3-opt--size') || main.closest('.wt-product__main');
        size?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        const first = size?.querySelector('label[for]:not(.disabled)') || size?.querySelector('label[for]');
        setTimeout(() => first?.focus({ preventScroll: true }), reduceMotion() ? 0 : 450);
        return;
      }
      if (!main.disabled) main.click();
    }

    onBack(e) {
      const g = this.gallery();
      if (!g) return;
      e.preventDefault();
      g.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      const first = g.querySelector('a[href], [tabindex="0"]');
      setTimeout(() => first?.focus({ preventScroll: true }), reduceMotion() ? 0 : 450);
    }
  }

  customElements.define('product-video-feature', ProductVideoFeature);
})();
